import React from "react";
import { useTranslation } from "react-i18next";
import useFetchInterval from "src/ui/hooks/useFetchInterval";

import { useFetchProductBuildsQuery } from "@dashboard/products/api";
import { Build } from "@dashboard/products/types";
import { Product } from "@dashboard/products/types/index";
import { SectionHeader } from "@dashboard/shared/styles";
import { Box, Paper, TableContainer, TableRow, Typography, useTheme } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import {
    createColumnHelper,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import { ArtifactsCell, BuildActionsCell, ChannelCell, StatusCell, TagsCell, TimestampCell } from "./Cells";
import { isBuildProcessing } from "./isBuildProcessing";

const columnHelper = createColumnHelper<Build>();

type ProductProps = {
    product: Product;
};
const BuildsTable = (props: ProductProps) => {
    const [sorting, setSorting] = React.useState<SortingState>([{ id: "version", desc: true }]);

    const theme = useTheme();
    const { t } = useTranslation(["common", "pages"]);

    const { data = [], isFetching, isError, refetch } = useFetchProductBuildsQuery(props.product.id);

    const hasProcessingBuilds = data.some(isBuildProcessing);

    useFetchInterval(refetch, { condition: hasProcessingBuilds });

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
    };

    const tableColumns = React.useMemo(
        () => [
            columnHelper.accessor("buildStatus", {
                header: "Status",
                cell: (info) => (
                    <StatusCell buildStatus={info.getValue()} build={info.row.original} productId={props.product.id} />
                ),
                enableSorting: true,
                meta: { align: "center" },
            }),
            columnHelper.accessor("version", {
                header: "Version",
                cell: (info) => (
                    <MinimalTableCell width={100}>
                        <Typography>{info.getValue()}</Typography>
                    </MinimalTableCell>
                ),
                enableSorting: true,
                sortingFn: (rowA, rowB) => SemverService.compare(rowA.original.version, rowB.original.version),
            }),
            columnHelper.accessor("created", {
                header: t("common:label.date"),
                cell: (info) => <TimestampCell created={info.getValue()} finished={info.row.original.built} />,
                enableSorting: true,
            }),
            columnHelper.accessor("artifacts", {
                header: "Artifacts",
                cell: (info) => <ArtifactsCell artifacts={info.getValue()} />,
                enableSorting: false,
            }),
            columnHelper.accessor("metadata", {
                id: "tags",
                header: t("common:label.tags"),
                cell: (info) => <TagsCell build={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.accessor("channel", {
                header: "Channel",
                cell: (info) => <ChannelCell build={info.row.original} />,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) => <BuildActionsCell build={info.row.original} productId={props.product.id} />,
                meta: { align: "right" },
            }),
        ],
        [props.product.id],
    );

    const table = useReactTable<Build>({
        data,
        columns: tableColumns,
        state: { sorting },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: onSortingChange,
    });

    return (
        <Box>
            <SectionHeader style={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(8) }}>
                {t("pages:productPage.listOfBuilds")}
            </SectionHeader>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={isFetching}
                        noDataMessage={t("pages:productPage.clickBuildToCreateNewRelease")}
                        error={{ isError, onError: refetch }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <React.Fragment key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </React.Fragment>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
        </Box>
    );
};

export default BuildsTable;
